/**
 * Modelo de dados de equipamento. Separa DEFINIÇÃO (imutável, no GameConfig) de
 * INSTÂNCIA (rolada, evolui, tem raridade/level/atributos). Preparado para expandir
 * (novos slots, tipos de arma, damage types) sem virar arquitetura enterprise.
 */
export type EquipmentSlot = "weapon" | "head" | "chest" | "gloves" | "boots" | "accessory";
// 10.9L25 — "tome" (Grimório, mago/necro) e "crossbow" (Besta, arqueiro): 2º tipo de arma da
// classe, pra ela conseguir ter 2 armas DISTINTAS ao mesmo tempo (igual guerreiro com espada+
// machado+maça) — antes só tinham 1 tipo, e toda cópia repetida virava shard na hora (nunca
// enchia a corrente). Ver GameConfig.classes (allowedWeapons) e GameConfig.mastery.bonuses.
export type WeaponType = "sword" | "axe" | "bow" | "mace" | "staff" | "tome" | "crossbow";
export type AttackType = "melee" | "ranged";
export type DamageType = "slashing" | "piercing" | "blunt" | "arcane"; // 10.9CC — arcano: cajado (mago/necromante), magias e servos
export type Rarity = "common" | "uncommon" | "rare" | "epic" | "legendary" | "mythic";

/** Atributos secundários que um item pode rolar. */
export type AttrKey =
  | "damage"
  | "attackSpeed" // %
  | "critChance" // %
  | "critDamage" // %
  | "maxHp"
  | "armor"
  | "moveSpeed" // %
  | "attackRange" // metros
  // 10.5T — equipamento também rola os atributos novos (antes só a Árvore dava)
  | "dmgSlash" // %
  | "dmgPierce" // %
  | "dmgBlunt" // %
  | "defSlash" // %
  | "defPierce" // %
  | "defBlunt" // %
  | "block" // % de chance
  | "regen" // fração do HP máx por segundo
  | "lifesteal" // 10.9EQ — fração do DANO causado que volta como vida
  | "maxHpPct" // 10.9FR — % de VIDA máxima (item forte)
  | "damagePct"; // 10.9FR — % de DANO

export interface RolledAttr {
  key: AttrKey;
  value: number;
}

export interface WeaponDefinition {
  id: string;
  nameKey: string;
  slot: "weapon";
  weaponType: WeaponType;
  attackType: AttackType;
  damageType: DamageType;
  baseDamage: number;
  attackRange: number;
  attacksPerSecond: number;
  arc: number;
  knockback: number;
  projectileSpeed: number; // usado por armas ranged
  visualId: string;
  bossOnly?: boolean; // 10.4P — só cai de BOSS (fora do pool de drop normal)
  raidOnly?: boolean; // 10.9GG — ÚNICO do boss GLOBAL (só cai na raid, fora do pool de boss normal)
  tier?: number; // 10.9FZ — tier de poder da arma (fim de jogo)
}

/** 10.6N — perigos ambientais de região (o Caçador precisa do anel certo para sobreviver). */
export type HazardKind = "frost" | "ember" | "gale" | "storm";

export interface ArmorDefinition {
  id: string;
  nameKey: string;
  slot: "head" | "chest" | "gloves" | "boots" | "accessory";
  visualId: string;
  baseArmor: number;
  baseHp: number;
  /** 10.6N — anel que PROTEGE deste perigo (sem ele o território te mata aos poucos). */
  protects?: HazardKind;
  /** 10.9AN/AO — TIER de PODER da armadura (escada por slot). Gate de NÍVEL pra dropar e equipar:
   * T2 = Nv20, T3 = Nv35, T4 = Nv50, T5 = Nv70, T6 = Nv90. Ausente = 1. */
  tier?: number; // 10.9FZ — tier de poder (1..14)
  raidOnly?: boolean; // 10.9GG — ÚNICO do boss GLOBAL
}

export type EquipmentDefinition = WeaponDefinition | ArmorDefinition;

export interface EquipmentInstance {
  instanceId: string;
  definitionId: string;
  slot: EquipmentSlot;
  rarity: Rarity;
  level: number;
  rolls: RolledAttr[];
  /** 10.9K — arma comprada com 💎 (VIP/boss): pode equipar IGNORANDO o nível mínimo por
   * raridade. É a vantagem de quem paga (drop normal continua respeitando o nível). */
  vip?: boolean;
}

/** Stats efetivos da arma equipada (lidos pelo AutoAttack). */
export interface EffectiveWeapon {
  weaponType: WeaponType;
  attackType: AttackType;
  damageType: DamageType;
  attackRange: number;
  attacksPerSecond: number;
  arc: number;
  knockback: number;
  projectileSpeed: number;
  visualId: string;
}

/** Bônus agregados (base + level + altar + equipment + mastery) aplicados ao Player. */
export interface StatMods {
  maxHp: number;
  maxHpMult: number; // multiplicador de HP (Void Tree)
  damage: number;
  armor: number;
  critChance: number; // fração somada
  critDamageMult: number; // fração somada
  moveSpeed: number; // ABSOLUTO (m/s) — ex.: altar agility
  moveSpeedMult: number; // multiplicador (equipment/mastery), 0.05 = +5%
  attackSpeedMult: number; // multiplicador (0.05 = +5%)
  attackRange: number; // metros somados
  damageMult: number; // multiplicador global de dano (mastery, tree, etc.)
  // Fase 6 (Void Tree): hook/sprint/condicionais
  hookRangeMult: number; // multiplicador (0.05 = +5%)
  hookCdReduc: number; // redução de cooldown do hook (0.08 = -8%)
  sprintDurationMult: number;
  sprintCdReduc: number;
  vsEliteMult: number; // dano extra vs Elite/Boss
  executeMult: number; // dano extra vs alvo abaixo do limiar
  resilience: number; // redução de dano recebido (0.1 = -10%)
  // 10.5K — atributos POR TIPO DE DANO + bloqueio + regeneração (Void Tree cobre tudo)
  dmgSlashPct: number; // +% de dano CORTANTE causado (espada/machado)
  dmgPiercePct: number; // +% de dano PERFURANTE causado (lança/arco)
  dmgBluntPct: number; // +% de dano CONTUNDENTE causado (clava/desarmado)
  dmgArcanePct: number; // 10.9CO — +% de dano ARCANO causado (cajado: mago/necromante)
  defSlashPct: number; // -% de dano cortante RECEBIDO
  defPiercePct: number; // -% de dano perfurante RECEBIDO
  defBluntPct: number; // -% de dano contundente RECEBIDO
  blockChance: number; // chance (0..1) de BLOQUEAR um golpe (reduz pelo combat.blockReduction)
  regenPct: number; // regeneração contínua: fração do HP máx. por segundo (também em combate)
  lifeStealPct: number; // 10.9EQ — ROUBO DE VIDA: fração do dano causado que cura o Caçador
}

export function emptyMods(): StatMods {
  return {
    maxHp: 0,
    maxHpMult: 0,
    damage: 0,
    armor: 0,
    critChance: 0,
    critDamageMult: 0,
    moveSpeed: 0,
    moveSpeedMult: 0,
    attackSpeedMult: 0,
    attackRange: 0,
    damageMult: 0,
    hookRangeMult: 0,
    hookCdReduc: 0,
    sprintDurationMult: 0,
    sprintCdReduc: 0,
    vsEliteMult: 0,
    executeMult: 0,
    resilience: 0,
    dmgSlashPct: 0,
    dmgPiercePct: 0,
    dmgBluntPct: 0,
    dmgArcanePct: 0,
    defSlashPct: 0,
    defPiercePct: 0,
    defBluntPct: 0,
    blockChance: 0,
    regenPct: 0,
    lifeStealPct: 0, // 10.9EQ
  };
}
